(function initDeliveryCalendarLoader(global) {
  'use strict';

  if (global.SnowcubesDeliveryCalendar) {
    if (typeof module !== 'undefined' && module.exports) {
      module.exports = global.SnowcubesDeliveryCalendar;
    }
    return;
  }

  var HOST_SELECTOR = 'delivery-calendar-picker';
  var CHECKOUT_SELECTOR = '[data-delivery-checkout-button]';
  var TIMEOUT_MS = 8000;
  var loadPromise = null;
  var state = 'idle';
  var observer = null;
  var started = false;
  var pendingActions = new WeakMap();

  function urls() {
    return global.SnowcubesDeliveryCalendarAssetUrls || {};
  }

  function rootContains(root, selector) {
    if (!root) return false;
    if (typeof root.matches === 'function' && root.matches(selector)) return true;
    return typeof root.querySelector === 'function' && Boolean(root.querySelector(selector));
  }

  function hosts(root) {
    var matches = [];
    if (!root) return matches;
    if (typeof root.matches === 'function' && root.matches(HOST_SELECTOR)) matches.push(root);
    if (typeof root.querySelectorAll === 'function') {
      matches = matches.concat(Array.from(root.querySelectorAll(HOST_SELECTOR)));
    }
    return matches.filter(function (host) {
      return host.isConnected;
    });
  }

  function isReady(root) {
    var targets = hosts(root || document);
    return Boolean(
      targets.length &&
      global.customElements &&
      global.customElements.get(HOST_SELECTOR) &&
      targets.every(function (host) {
        return host.dataset.deliveryCalendarReady === 'true';
      })
    );
  }

  function withTimeout(setup, message) {
    return new Promise(function (resolve, reject) {
      var settled = false;
      var cleanup = function () {};
      var timer = global.setTimeout(function () {
        if (settled) return;
        settled = true;
        cleanup();
        reject(new Error(message));
      }, TIMEOUT_MS);

      cleanup = setup(
        function (value) {
          if (settled) return;
          settled = true;
          global.clearTimeout(timer);
          resolve(value);
        },
        function (error) {
          if (settled) return;
          settled = true;
          global.clearTimeout(timer);
          reject(error);
        }
      ) || cleanup;
    });
  }

  function loadStylesheet(url) {
    if (!url) return Promise.reject(new Error('Delivery calendar stylesheet URL is missing'));

    var existing = document.querySelector('link[data-snowcubes-delivery-calendar-style]');
    if (existing && existing.dataset.loaded === 'true') return Promise.resolve(existing);

    return withTimeout(function (resolve, reject) {
      var link = existing || document.createElement('link');
      var onLoad = function () {
        link.dataset.loaded = 'true';
        resolve(link);
      };
      var onError = function () {
        link.remove();
        reject(new Error('Failed to load delivery calendar stylesheet'));
      };

      link.addEventListener('load', onLoad, { once: true });
      link.addEventListener('error', onError, { once: true });
      if (!existing) {
        link.rel = 'stylesheet';
        link.href = url;
        link.dataset.snowcubesDeliveryCalendarStyle = 'true';
        document.head.appendChild(link);
      }
      return function () {
        link.removeEventListener('load', onLoad);
        link.removeEventListener('error', onError);
        if (link.dataset.loaded !== 'true') link.remove();
      };
    }, 'Timed out loading delivery calendar stylesheet');
  }

  function loadScript(url) {
    if (global.customElements && global.customElements.get(HOST_SELECTOR)) {
      return Promise.resolve(null);
    }
    if (!url) return Promise.reject(new Error('Delivery calendar component URL is missing'));

    var existing = document.querySelector('script[data-snowcubes-delivery-calendar-script]');
    return withTimeout(function (resolve, reject) {
      var script = existing || document.createElement('script');
      var onLoad = function () {
        if (!global.customElements || !global.customElements.get(HOST_SELECTOR)) {
          script.remove();
          reject(new Error('Delivery calendar component did not initialize'));
          return;
        }
        resolve(script);
      };
      var onError = function () {
        script.remove();
        reject(new Error('Failed to load delivery calendar component'));
      };

      script.addEventListener('load', onLoad, { once: true });
      script.addEventListener('error', onError, { once: true });
      if (!existing) {
        script.src = url;
        script.async = false;
        script.dataset.snowcubesDeliveryCalendarScript = 'true';
        document.head.appendChild(script);
      }
      return function () {
        script.removeEventListener('load', onLoad);
        script.removeEventListener('error', onError);
        if (!global.customElements || !global.customElements.get(HOST_SELECTOR)) script.remove();
      };
    }, 'Timed out loading delivery calendar component');
  }

  function waitForReady(root) {
    if (isReady(root)) return Promise.resolve();

    return withTimeout(function (resolve) {
      var onReady = function () {
        if (!isReady(root)) return;
        document.removeEventListener('delivery-calendar:ready', onReady);
        resolve();
      };
      document.addEventListener('delivery-calendar:ready', onReady);
      // Defining a custom element upgrades connected hosts synchronously. Check
      // once more after the listener is installed to close that event gap.
      onReady();
      return function () {
        document.removeEventListener('delivery-calendar:ready', onReady);
      };
    }, 'Timed out initializing delivery calendar');
  }

  function ensureLoaded(root) {
    var targetRoot = root || document;
    if (!rootContains(targetRoot, HOST_SELECTOR)) return Promise.resolve(null);
    if (isReady(targetRoot)) return Promise.resolve(api);
    if (loadPromise) {
      return loadPromise.then(function () {
        return waitForReady(targetRoot).then(function () {
          return api;
        });
      });
    }

    state = 'loading';
    var assetUrls = urls();
    loadPromise = loadStylesheet(assetUrls.stylesheet)
      .then(function () {
        return loadScript(assetUrls.component);
      })
      .then(function () {
        return waitForReady(document);
      })
      .then(function () {
        state = 'ready';
        return api;
      })
      .catch(function (error) {
        state = 'idle';
        loadPromise = null;
        throw error;
      });

    return loadPromise.then(function () {
      return waitForReady(targetRoot).then(function () {
        return api;
      });
    });
  }

  function errorElement(root) {
    var host = root && root.matches && root.matches(HOST_SELECTOR)
      ? root
      : root && root.querySelector
        ? root.querySelector(HOST_SELECTOR)
        : null;
    if (!host) return null;

    var error = host.querySelector('[data-delivery-calendar-load-error]');
    if (!error) {
      error = host.querySelector('.delivery-calendar-picker-v2__error');
    }
    if (!error) {
      error = document.createElement('p');
      error.className = 'delivery-calendar-picker-v2__error';
      host.appendChild(error);
    }
    error.dataset.deliveryCalendarLoadError = 'true';
    error.setAttribute('role', 'alert');
    return error;
  }

  function showError(root, message) {
    var error = errorElement(root);
    if (!error) return;
    error.hidden = false;
    error.textContent = message || "We couldn't load the ship-date picker. Please try again.";
  }

  function clearError(root) {
    var error = root && root.querySelector
      ? root.querySelector('[data-delivery-calendar-load-error]')
      : null;
    if (!error) return;
    error.hidden = true;
    error.textContent = '';
  }

  function scan(root) {
    var targetRoot = root || document;
    if (!rootContains(targetRoot, HOST_SELECTOR)) return null;
    var request = ensureLoaded(targetRoot);
    request.catch(function () {
      showError(targetRoot);
    });
    return request;
  }

  function actionContext(target) {
    var form = target && target.closest ? target.closest('form') : null;
    var host = form && form.querySelector(HOST_SELECTOR);
    return { form: form, host: host };
  }

  function shipDateIsSelected(form) {
    var input = form && form.querySelector('input[name="attributes[ship_date]"]');
    return Boolean(input && input.value);
  }

  function finishAction(key, busyTarget) {
    pendingActions.delete(key);
    if (busyTarget) busyTarget.removeAttribute('aria-busy');
  }

  function blockUntilReady(key, busyTarget, context, replay) {
    if (pendingActions.has(key)) return;
    busyTarget.setAttribute('aria-busy', 'true');
    clearError(context.form);

    var request = ensureLoaded(context.form)
      .then(function () {
        finishAction(key, busyTarget);
        if (!context.form.isConnected || !context.host.isConnected) return;
        if (!shipDateIsSelected(context.form)) {
          showError(context.form, context.host.dataset.requiredMessage || 'Please select a ship date to continue.');
          if (typeof context.host.scrollIntoView === 'function') {
            context.host.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          return;
        }
        replay();
      })
      .catch(function () {
        finishAction(key, busyTarget);
        if (!context.form.isConnected) return;
        showError(context.form);
        if (busyTarget.isConnected && typeof busyTarget.focus === 'function') busyTarget.focus();
      });

    pendingActions.set(key, request);
  }

  function handleClick(event) {
    var target = event.target instanceof Element ? event.target.closest(CHECKOUT_SELECTOR) : null;
    if (!target) return;
    var context = actionContext(target);
    if (!context.host || isReady(context.form)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    blockUntilReady(target, target, context, function () {
      if (target.isConnected) target.click();
    });
  }

  function handleSubmit(event) {
    var form = event.target instanceof HTMLFormElement ? event.target : null;
    var host = form && form.querySelector(HOST_SELECTOR);
    var submitter = event.submitter;
    if (
      !host
      || !submitter
      || !submitter.matches(CHECKOUT_SELECTOR)
      || isReady(form)
    ) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    blockUntilReady(form, form, { form: form, host: host }, function () {
      if (!form.isConnected) return;
      if (submitter.isConnected) form.requestSubmit(submitter);
    });
  }

  function start() {
    if (started) return;
    started = true;
    document.addEventListener('click', handleClick, true);
    document.addEventListener('submit', handleSubmit, true);
    observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.from(mutation.addedNodes).forEach(function (node) {
          if (node.nodeType === 1) scan(node);
        });
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
    scan(document);
  }

  function stop() {
    document.removeEventListener('click', handleClick, true);
    document.removeEventListener('submit', handleSubmit, true);
    if (observer) observer.disconnect();
    observer = null;
    started = false;
  }

  var api = {
    ensureLoaded: ensureLoaded,
    scan: scan,
    isReady: isReady,
    showError: showError,
    clearError: clearError,
    loadStylesheet: loadStylesheet,
    loadScript: loadScript,
    start: start,
    stop: stop,
    get state() {
      return state;
    },
  };

  global.SnowcubesDeliveryCalendar = api;
  start();

  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})(typeof window !== 'undefined' ? window : globalThis);
